// =============================================================================
// ICON COMPONENTS - Lucide React icons with consistent styling
// =============================================================================

import {
  MessageCircle,
  Phone,
  Video,
  Users,
  Camera,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsRight,
  Search,
  MoreVertical,
  Plus,
  X,
  Check,
  CheckCheck,
  Circle,
  AlertCircle,
  Send,
  Paperclip,
  Image,
  Folder,
  FolderOpen,
  Upload,
  Download,
  Trash2,
  Edit,
  Star,
  Heart,
  Smile,
  Bell,
  BellOff,
  Pin,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  RotateCcw,
  Menu,
  Home,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Loader2,
  ImageIcon,
  FileText,
  Film,
  Music,
  Mic,
  MicOff,
  VideoOff,
  PhoneOff,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Share,
  Link,
  ExternalLink,
  Info,
  HelpCircle,
  Shield,
  ShieldCheck,
  User,
  UserPlus,
  UserMinus,
  Crown,
  Play,
  Pause,
  Images,
  Globe,
  Cloud,
  Key,
  Cpu,
  Database,
  Server,
  Layers,
  Zap,
  Code,
  GitBranch,
  Box,
  Sparkles,
  Smartphone,
  Monitor,
  Tablet,
  Terminal,
  FileCode,
  Wifi,
  HardDrive,
  CalendarDays,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  MessageSquare,
  AtSign,
  Forward,
  Clock,
  Megaphone,
} from 'lucide-react';
import { forwardRef } from 'react';

// Re-export all icons for easy importing
export {
  MessageCircle,
  Phone,
  Video,
  Users,
  Camera,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronsRight,
  Search,
  MoreVertical,
  Plus,
  X,
  Check,
  CheckCheck,
  Circle,
  AlertCircle,
  Send,
  Paperclip,
  Image,
  Folder,
  FolderOpen,
  Upload,
  Download,
  Trash2,
  Edit,
  Star,
  Heart,
  Smile,
  Bell,
  BellOff,
  Pin,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  RefreshCw,
  RotateCcw,
  Menu,
  Home,
  ArrowLeft,
  ArrowRight,
  ArrowDown,
  ArrowUp,
  Loader2,
  ImageIcon,
  FileText,
  Film,
  Music,
  Mic,
  MicOff,
  VideoOff,
  PhoneOff,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Share,
  Link,
  ExternalLink,
  Info,
  HelpCircle,
  Shield,
  ShieldCheck,
  User,
  UserPlus,
  UserMinus,
  Crown,
  Play,
  Pause,
  Images,
  Globe,
  Cloud,
  Key,
  Cpu,
  Database,
  Server,
  Layers,
  Zap,
  Code,
  GitBranch,
  Box,
  Sparkles,
  Smartphone,
  Monitor,
  Tablet,
  Terminal,
  FileCode,
  Wifi,
  HardDrive,
  CalendarDays,
  PhoneIncoming,
  PhoneOutgoing,
  PhoneMissed,
  MessageSquare,
  AtSign,
  Forward,
  Clock,
  Megaphone,
};

// Custom Shamrock icon (Chlann logo) - uses deployed SVG favicon
export const ShamrockIcon = forwardRef<HTMLImageElement, { className?: string }>(
  ({ className, ...props }, ref) => (
    <img
      ref={ref}
      src="/favicon.svg"
      alt="Chlann"
      className={className}
      draggable={false}
      {...props}
    />
  )
);
ShamrockIcon.displayName = 'ShamrockIcon';

// Chlann app icon (256x256 dark rounded square)
export const ChlannAppIcon = forwardRef<HTMLImageElement, { className?: string }>(
  ({ className, ...props }, ref) => (
    <img
      ref={ref}
      src="/chlann-icon.svg"
      alt="Chlann"
      className={className}
      draggable={false}
      {...props}
    />
  )
);
ChlannAppIcon.displayName = 'ChlannAppIcon';

// Chlann horizontal logo (440x100, for headers/nav bars)
export const ChlannHorizontalLogo = forwardRef<HTMLImageElement, { className?: string }>(
  ({ className, ...props }, ref) => (
    <img
      ref={ref}
      src="/chlann-horizontal.svg"
      alt="Chlann"
      className={className}
      draggable={false}
      {...props}
    />
  )
);
ChlannHorizontalLogo.displayName = 'ChlannHorizontalLogo';

// Chlann full logo (512x512, with wordmark)
export const ChlannFullLogo = forwardRef<HTMLImageElement, { className?: string }>(
  ({ className, ...props }, ref) => (
    <img
      ref={ref}
      src="/logo.webp"
      alt="Chlann"
      className={className}
      draggable={false}
      {...props}
    />
  )
);
ChlannFullLogo.displayName = 'ChlannFullLogo';

// =============================================================================
// Icon size presets
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export const iconSizes: Record<IconSize, string> = {
  xs: 'w-3 h-3',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-8 h-8',
};

// Helper to get icon class
export function getIconClass(size: IconSize = 'md', className?: string): string {
  return `${iconSizes[size]} ${className || ''}`.trim();
}
